import React, { useMemo } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Tag, HStack, Text, Tooltip, Box } from "@chakra-ui/react";
import { pageLabel, activityLabel } from "../../../utils/pageLabels";
import { formatAgo, formatDuration, formatBytes } from "./format";

export default function LiveUsersTable({ users }) {
  const rows = useMemo(() => {
    const list = [...(users || [])];
    list.sort((a, b) => {
      if (a.state !== b.state) return a.state === "online" ? -1 : 1;
      return Date.parse(b.last_seen || 0) - Date.parse(a.last_seen || 0);
    });
    return list;
  }, [users]);

  if (rows.length === 0) return <Text fontSize="sm" color="gray.500">Nobody has been online in this window.</Text>;

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>User</Th><Th>Clinic · role</Th><Th>Page</Th><Th>Doing</Th><Th>Session length</Th><Th>Last seen</Th>
            <Th isNumeric>AI questions</Th><Th isNumeric>AI tokens*</Th><Th isNumeric>Recordings</Th><Th isNumeric>Note edits</Th>
            <Th isNumeric>Requests</Th><Th isNumeric>Heavy requests</Th><Th isNumeric>Memory added</Th><Th isNumeric>Data transferred</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((u) => (
            <Tr key={`${u.user_id}-${u.session_id}`} opacity={u.state === "online" ? 1 : 0.55}>
              <Td>
                <Text fontWeight="semibold">{u.name || u.email || u.user_id}</Text>
                <Text fontSize="xs" color="gray.500">{u.email}</Text>
              </Td>
              <Td><Text fontSize="xs">{u.schema}</Text><Text fontSize="xs" color="gray.500">{u.role}</Text></Td>
              <Td>
                <Tooltip label={u.full_path || u.page} hasArrow>
                  <Text>{pageLabel(u.page)}</Text>
                </Tooltip>
              </Td>
              <Td>
                <HStack spacing={1} flexWrap="wrap">
                  {(u.activities || []).map((a) => <Tag key={a} size="sm" colorScheme="purple">{activityLabel(a)}</Tag>)}
                  {u.state === "online" && !u.active && <Tag size="sm" colorScheme="gray">idle</Tag>}
                  {u.visible === false && <Tag size="sm" colorScheme="gray">background tab</Tag>}
                </HStack>
              </Td>
              <Td>
                <Text fontSize="xs">
                  {u.session_started_at ? formatDuration(Date.parse(u.last_seen) - Date.parse(u.session_started_at)) : "—"}
                </Text>
              </Td>
              <Td><Text fontSize="xs">{formatAgo(u.last_seen)}</Text></Td>
              <Td isNumeric>{u.counts?.ai_queries ?? 0}</Td>
              <Td isNumeric>{u.tokens?.total_tokens ? u.tokens.total_tokens.toLocaleString() : 0}</Td>
              <Td isNumeric>{(u.counts?.audio_records ?? 0) + (u.counts?.transcripts ?? 0)}</Td>
              <Td isNumeric>{u.counts?.document_edits ?? 0}</Td>
              <Td isNumeric>{u.requests?.count ?? 0}</Td>
              <Td isNumeric>{u.requests?.heavy_count ?? 0}</Td>
              <Td isNumeric>{u.requests?.rss_delta_mb ? `+${Math.round(u.requests.rss_delta_mb)} MB` : "—"}</Td>
              <Td isNumeric>{formatBytes(u.requests?.bytes)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Text fontSize="xs" color="gray.500" mt={1}>*AI tokens are counted per user since the session started, not per tab. "Memory added" is how much server memory grew while that session's requests ran.</Text>
    </Box>
  );
}
